import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { getExpiringSubscriptions } from '../../Api/Platform';
import TrinityDashboardModule from './TrinityDashboardModule';

const WINDOWS = [
  { value: '7', label: '7 days' },
  { value: '14', label: '14 days' },
  { value: '30', label: '30 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
  { value: 'all', label: 'All' },
];

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const csvEscape = (value) => {
  const s = value == null ? '' : String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const rowToCsv = (row) => ([
  row.organization_name,
  row.subscription_status,
  row.subscription_remaining_days,
  row.is_paid_client ? 'Paid' : 'Trial',
  row.expires_at || '',
  row.user_count,
  row.last_user_login_at || '',
  row.created_at || '',
  row.country || '',
  row.onboarding_completed ? 'yes' : 'no',
  row.is_test ? 'test' : '',
  row.is_demo ? 'demo' : '',
  row.stripe_customer_id || '',
].map(csvEscape).join(','));

const buildQuery = ({ windowDays, q, paid, status, includeTest, limit, offset, sort }) => {
  const params = {
    paid,
    status,
    include_test: includeTest,
    limit,
    offset,
    sort,
  };
  if (q) params.q = q;
  if (windowDays === 'all') {
    params.all = true;
  } else {
    params.within_days = Number(windowDays);
  }
  return params;
};

const ExpiringSubscriptionsPanel = ({ expanded, onChange }) => {
  const [windowDays, setWindowDays] = useState('30');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [paid, setPaid] = useState('all');
  const [status, setStatus] = useState('all');
  const [includeTest, setIncludeTest] = useState(true);
  const [sort, setSort] = useState('remaining_days');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 400);
    return () => clearTimeout(t);
  }, [qInput]);

  useEffect(() => {
    setPage(0);
  }, [windowDays, q, paid, status, includeTest, sort, pageSize]);

  const query = useMemo(
    () => buildQuery({
      windowDays,
      q,
      paid,
      status,
      includeTest,
      limit: pageSize,
      offset: page * pageSize,
      sort,
    }),
    [windowDays, q, paid, status, includeTest, pageSize, page, sort],
  );

  const load = useCallback(() => {
    setLoading(true);
    getExpiringSubscriptions(query)
      .then((res) => {
        setRows(res?.data?.data || []);
        setTotal(res?.data?.total || 0);
      })
      .catch(() => {
        setRows([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  const expiredCount = rows.filter((r) => r.subscription_status === 'expired').length;

  const exportCsv = async () => {
    setExporting(true);
    try {
      const all = [];
      let offset = 0;
      const limit = 100;
      const base = buildQuery({
        windowDays,
        q,
        paid,
        status,
        includeTest,
        limit,
        offset: 0,
        sort,
      });
      while (true) {
        const res = await getExpiringSubscriptions({ ...base, limit, offset });
        const batch = res?.data?.data || [];
        const matchTotal = res?.data?.total || 0;
        all.push(...batch);
        offset += batch.length;
        if (batch.length === 0 || all.length >= matchTotal) break;
      }
      const header = [
        'organization_name',
        'status',
        'remaining_days',
        'paid_or_trial',
        'expires_at',
        'user_count',
        'last_user_login_at',
        'created_at',
        'country',
        'onboarding_completed',
        'is_test',
        'is_demo',
        'stripe_customer_id',
      ].join(',');
      const blob = new Blob([[header, ...all.map(rowToCsv)].join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'expiring-subscriptions.csv';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const summary = `${total} matching${loading ? ' · loading' : ''}`;

  return (
    <TrinityDashboardModule
      title="Subscriptions expiring"
      summary={summary}
      expanded={expanded}
      onChange={onChange}
    >
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <Chip size="small" label={`${total} matching`} />
        <Chip size="small" color="warning" variant="outlined" label={`${expiredCount} expired on this page`} />
        <TextField
          size="small"
          label="Search organisations"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Window</InputLabel>
          <Select
            label="Window"
            value={windowDays}
            onChange={(e) => setWindowDays(e.target.value)}
          >
            {WINDOWS.map((w) => (
              <MenuItem key={String(w.value)} value={w.value}>{w.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Paid</InputLabel>
          <Select label="Paid" value={paid} onChange={(e) => setPaid(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="trial">Trial</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
            <MenuItem value="active">Active</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sort</InputLabel>
          <Select label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
            <MenuItem value="remaining_days">Days left</MenuItem>
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="created_at">Created</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={(
            <Checkbox
              checked={!includeTest}
              onChange={(e) => setIncludeTest(!e.target.checked)}
            />
          )}
          label="Hide test / demo"
        />
        <Button size="small" variant="outlined" onClick={exportCsv} disabled={exporting || total === 0}>
          {exporting ? 'Exporting…' : 'Download CSV'}
        </Button>
      </Box>

      <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 1 }}>
        Remaining days use the organisation trial clock, not Stripe period end.
      </Typography>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Organisation</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Days left</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell>Users</TableCell>
              <TableCell>Last login</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Onboarding</TableCell>
              <TableCell>Flags</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11}>
                  <Typography variant="body2" color="text.secondary">
                    {loading ? 'Loading…' : 'No matching organisations'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Link
                    component={RouterLink}
                    to={`/organizations?organization=${encodeURIComponent(row.id)}`}
                    underline="hover"
                  >
                    {row.organization_name}
                  </Link>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.subscription_status}
                    color={row.subscription_status === 'expired' ? 'warning' : 'success'}
                  />
                </TableCell>
                <TableCell>{row.subscription_remaining_days}d</TableCell>
                <TableCell>{row.is_paid_client ? 'Paid' : 'Trial'}</TableCell>
                <TableCell>{formatDate(row.expires_at)}</TableCell>
                <TableCell>{row.user_count ?? 0}</TableCell>
                <TableCell>{formatDateTime(row.last_user_login_at)}</TableCell>
                <TableCell>{formatDate(row.created_at)}</TableCell>
                <TableCell>{row.country || '—'}</TableCell>
                <TableCell>{row.onboarding_completed ? 'Done' : 'Incomplete'}</TableCell>
                <TableCell>
                  {row.is_test ? 'test' : ''}
                  {row.is_test && row.is_demo ? ' / ' : ''}
                  {row.is_demo ? 'demo' : ''}
                  {!row.is_test && !row.is_demo ? '—' : ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, next) => setPage(next)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          setPageSize(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </TrinityDashboardModule>
  );
};

export default ExpiringSubscriptionsPanel;
